import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Lock,
  FileCheck,
  Users,
  Building2,
  Zap,
  CheckCircle2,
  ArrowRight,
  Database,
  Eye,
  EyeOff,
  ChevronDown,
  Star,
  TrendingUp,
  Clock,
  Award,
  Fingerprint,
  Wallet,
  BadgeCheck,
  Server,
  Code2,
  Sparkles,
  ArrowDown,
  ExternalLink,
  ShieldCheck,
} from "lucide-react";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";

// Stakeholder types for journey selection
type Stakeholder = "issuer" | "verifier" | "subject" | null;

// ============================================================================
// Reusable Components
// ============================================================================

// Animated gradient text
function GradientText({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`bg-gradient-to-r from-primary-500 via-trust-teal to-success-500 bg-clip-text text-transparent ${className}`}>
      {children}
    </span>
  );
}

// Floating badge component
function FloatingBadge({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg border border-slate-200/50 ${className}`}>
      {children}
    </div>
  );
}

// Feature card with hover effects
function FeatureCard({
  icon: Icon,
  title,
  description,
  gradient,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  gradient: string;
}) {
  return (
    <div className="group relative bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:shadow-xl hover:border-transparent hover:-translate-y-1 transition-all duration-300 overflow-hidden">
      {/* Gradient overlay on hover */}
      <div className={`absolute inset-0 ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
      
      <div className="relative z-10">
        <div className={`w-14 h-14 rounded-2xl ${gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-3">{title}</h3>
        <p className="text-slate-600 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

// How it works step
function HowItWorksStep({
  step,
  icon: Icon,
  title,
  description,
  isLast = false,
}: {
  step: number;
  icon: React.ElementType;
  title: string;
  description: string;
  isLast?: boolean;
}) {
  return (
    <div className="relative flex flex-col items-center text-center">
      {/* Connector line */}
      {!isLast && (
        <div className="hidden md:block absolute top-10 left-[calc(50%+3rem)] w-[calc(100%-6rem)] h-0.5 bg-gradient-to-r from-primary-200 via-trust-teal/30 to-success-200" />
      )}
      
      {/* Step circle */}
      <div className="relative">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-success-500 flex items-center justify-center shadow-lg shadow-primary-500/25">
          <Icon className="w-9 h-9 text-white" />
        </div>
        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center justify-center shadow-lg">
          {step}
        </div>
      </div>
      
      <h3 className="mt-6 text-xl font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-slate-600 max-w-xs">{description}</p>
    </div>
  );
}

// Credential type card - enhanced
function CredentialTypeCard({
  type,
  title,
  description,
  useCases,
  gradient,
  icon: Icon,
  circuit,
}: {
  type: string;
  title: string;
  description: string;
  useCases: string[];
  gradient: string;
  icon: React.ElementType;
  circuit: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="group bg-white rounded-2xl shadow-sm border border-slate-200 hover:shadow-xl hover:border-transparent transition-all duration-300 overflow-hidden">
      {/* Top gradient bar */}
      <div className={`h-1.5 ${gradient}`} />
      
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-2xl ${gradient} flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform duration-300`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-1 text-xs font-mono font-medium rounded-md bg-slate-100 text-slate-700">
                {type}
              </span>
              <span className="px-2.5 py-1 text-xs font-mono rounded-md bg-purple-50 text-purple-600">
                {circuit}
              </span>
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">{title}</h3>
            <p className="text-slate-600 text-sm">{description}</p>
          </div>
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-5 flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          {isExpanded ? "Hide" : "View"} use cases
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
        </button>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-slate-100 animate-fade-in">
            <ul className="space-y-2">
              {useCases.map((useCase, index) => (
                <li key={index} className="flex items-center gap-2.5 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-success-500 flex-shrink-0" />
                  {useCase}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// Journey step component
function JourneyStep({
  step,
  title,
  description,
  isLast = false,
}: {
  step: number;
  title: string;
  description: string;
  isLast?: boolean;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center font-semibold text-sm shadow-lg shadow-primary-500/25">
          {step}
        </div>
        {!isLast && <div className="w-0.5 h-full bg-gradient-to-b from-primary-200 to-transparent mt-2 min-h-[2rem]" />}
      </div>
      <div className={isLast ? "" : "pb-6"}>
        <h4 className="font-semibold text-slate-900">{title}</h4>
        <p className="text-sm text-slate-600 mt-1">{description}</p>
      </div>
    </div>
  );
}

// Stats component - enhanced
function StatCard({
  value,
  label,
  icon: Icon,
}: {
  value: string;
  label: string;
  icon: React.ElementType;
}) {
  return (
    <div className="text-center group">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/10 mb-4 group-hover:bg-white/20 transition-colors duration-300">
        <Icon className="w-7 h-7 text-white" />
      </div>
      <div className="text-4xl font-bold text-white">{value}</div>
      <div className="text-sm text-white/70 mt-1">{label}</div>
    </div>
  );
}

// Tech stack item
function TechItem({ icon: Icon, name }: { icon: React.ElementType; name: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 rounded-lg border border-slate-700">
      <Icon className="w-5 h-5 text-slate-400" />
      <span className="text-sm text-slate-300 font-medium">{name}</span>
    </div>
  );
}

// ============================================================================
// Main Landing Page Component
// ============================================================================

export function Landing() {
  const [selectedStakeholder, setSelectedStakeholder] = useState<Stakeholder>(null);

  // Stakeholder journeys
  const journeys = {
    issuer: [
      { title: "Create Enterprise Account", description: "Sign up and configure your organization settings in minutes." },
      { title: "Generate API Keys", description: "Create API keys with granular permissions for secure integration." },
      { title: "Issue Credentials", description: "Issue privacy-preserving credentials with on-chain commitments." },
      { title: "Monitor & Manage", description: "Track usage, handle revocations, and view comprehensive analytics." },
    ],
    verifier: [
      { title: "Integrate ZKCN SDK", description: "Add our TypeScript SDK to your application in minutes." },
      { title: "Request Verification", description: "Send verification requests to users via API or QR code." },
      { title: "Verify ZK Proofs", description: "Validate proofs cryptographically using our smart contracts." },
      { title: "Access Audit Trail", description: "Review complete, tamper-proof verification history." },
    ],
    subject: [
      { title: "Receive Credential", description: "Get issued a privacy-preserving credential from an authorized party." },
      { title: "Store in Wallet", description: "Securely store your credential and secret salt in your wallet." },
      { title: "Generate ZK Proof", description: "Create proofs that verify attributes without revealing data." },
      { title: "Share Selectively", description: "Share only what's needed. You control your data." },
    ],
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <Header variant="landing" />

      {/* ================================================================== */}
      {/* HERO SECTION */}
      {/* ================================================================== */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-80 h-80 bg-success-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 left-1/2 w-[600px] h-[600px] bg-trust-teal/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* Live badge */}
            <FloatingBadge className="mb-8 animate-fade-in">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-success-500" />
              </span>
              <span className="text-sm font-medium text-slate-700">
                Live on Base Sepolia Testnet
              </span>
            </FloatingBadge>

            {/* Main headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-slate-900 leading-[1.1] tracking-tight">
              Verify Without{" "}
              <span className="relative">
                <GradientText>Revealing</GradientText>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                  <path d="M2 10C50 4 100 4 150 6C200 8 250 4 298 10" stroke="url(#underline-gradient)" strokeWidth="3" strokeLinecap="round" />
                  <defs>
                    <linearGradient id="underline-gradient" x1="0" y1="0" x2="300" y2="0">
                      <stop stopColor="#3b82f6" />
                      <stop offset="0.5" stopColor="#14b8a6" />
                      <stop offset="1" stopColor="#22c55e" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>
            </h1>

            {/* Subheadline */}
            <p className="mt-8 text-lg sm:text-xl lg:text-2xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              ZKCN is a <strong className="text-slate-900">Zero-Knowledge Credential Network</strong> that lets you 
              verify any sensitive attribute <em>without exposing personal data</em>.
            </p>

            {/* Value props */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-200">
                <EyeOff className="w-4 h-4 text-primary-500" />
                <span className="text-slate-700">Data Never Leaves Your Device</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-200">
                <Lock className="w-4 h-4 text-success-500" />
                <span className="text-slate-700">Cryptographically Proven</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-200">
                <Database className="w-4 h-4 text-purple-500" />
                <span className="text-slate-700">Blockchain Anchored</span>
              </div>
            </div>

            {/* CTA buttons */}
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/signin"
                className="group w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all flex items-center justify-center gap-2"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/docs"
                className="w-full sm:w-auto px-8 py-4 bg-white hover:bg-slate-50 text-slate-700 font-semibold rounded-xl border border-slate-200 shadow-sm hover:shadow-md flex items-center justify-center gap-2 transition-all"
              >
                <Code2 className="w-5 h-5" />
                View Documentation
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="mt-14 flex flex-wrap items-center justify-center gap-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-success-500" />
                <span>SOC 2 Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-success-500" />
                <span>GDPR Ready</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-success-500" />
                <span>HIPAA Compatible</span>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="mt-16 flex justify-center animate-bounce">
            <a href="#what-is-zkcn" className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow">
              <ArrowDown className="w-5 h-5 text-slate-400" />
            </a>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* WHAT IS ZKCN SECTION */}
      {/* ================================================================== */}
      <section id="what-is-zkcn" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Explanation */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 rounded-full text-primary-700 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                What is Zero-Knowledge?
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                Prove Facts Without{" "}
                <GradientText>Revealing Secrets</GradientText>
              </h2>
              
              <div className="mt-6 space-y-4 text-lg text-slate-600">
                <p>
                  <strong className="text-slate-900">Zero-knowledge proofs (ZKPs)</strong> are a cryptographic breakthrough 
                  that lets you prove something is true without revealing the underlying data.
                </p>
                <p>
                  With ZKCN, a user can prove they're over 21 <em>without revealing their birthdate</em>, 
                  or prove they have proper permissions <em>without exposing access details</em>.
                </p>
              </div>

              {/* Example box */}
              <div className="mt-8 p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-primary-500" />
                  Traditional vs Zero-Knowledge
                </h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                    <div className="text-xs font-medium text-red-600 mb-2">❌ TRADITIONAL</div>
                    <p className="text-sm text-red-800">
                      "My birthdate is Jan 15, 1990, so I'm over 21"
                    </p>
                    <p className="text-xs text-red-500 mt-2">→ Exposes exact birthdate</p>
                  </div>
                  <div className="p-4 bg-success-50 rounded-xl border border-success-100">
                    <div className="text-xs font-medium text-success-600 mb-2">✓ ZERO-KNOWLEDGE</div>
                    <p className="text-sm text-success-800">
                      "I can cryptographically prove I'm over 21"
                    </p>
                    <p className="text-xs text-success-500 mt-2">→ Reveals nothing else</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Visual diagram */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-trust-teal/10 to-success-500/10 rounded-3xl blur-2xl" />
              <div className="relative bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
                <h4 className="font-semibold text-slate-900 mb-6 text-center">How ZKCN Works</h4>
                
                <div className="space-y-6">
                  {/* Step 1 */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">1. Issuer Creates Credential</div>
                      <p className="text-sm text-slate-600 mt-1">
                        A trusted issuer (e.g., DMV, hospital) creates a credential with your data
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Database className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">2. Commitment Stored On-Chain</div>
                      <p className="text-sm text-slate-600 mt-1">
                        Only a cryptographic hash (commitment) goes on blockchain—your data stays private
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-success-100 flex items-center justify-center flex-shrink-0">
                      <Fingerprint className="w-6 h-6 text-success-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">3. User Generates ZK Proof</div>
                      <p className="text-sm text-slate-600 mt-1">
                        When verification is needed, user generates a proof locally in their browser
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <BadgeCheck className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">4. Verifier Validates Proof</div>
                      <p className="text-sm text-slate-600 mt-1">
                        Verifier confirms the proof on-chain—learning only that the claim is true
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bottom stats */}
                <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary-600">&lt;50ms</div>
                    <div className="text-xs text-slate-500">Proof generation</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-success-600">100%</div>
                    <div className="text-xs text-slate-500">Data privacy</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">On-chain</div>
                    <div className="text-xs text-slate-500">Verification</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* HOW IT WORKS - VISUAL STEPS */}
      {/* ================================================================== */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Simple Integration, <GradientText>Powerful Privacy</GradientText>
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Get up and running in minutes with our developer-friendly SDK and REST APIs.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 md:gap-4">
            <HowItWorksStep
              step={1}
              icon={Building2}
              title="Issue Credential"
              description="Trusted issuer creates a credential and registers its commitment on-chain."
            />
            <HowItWorksStep
              step={2}
              icon={Wallet}
              title="Store in Wallet"
              description="User receives credential and stores it securely in their local wallet."
            />
            <HowItWorksStep
              step={3}
              icon={Fingerprint}
              title="Generate Proof"
              description="When needed, user generates a ZK proof in their browser—data never leaves."
            />
            <HowItWorksStep
              step={4}
              icon={BadgeCheck}
              title="Verify On-Chain"
              description="Verifier validates the proof using our smart contracts. Done!"
              isLast
            />
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* FEATURES SECTION */}
      {/* ================================================================== */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Enterprise-Grade <GradientText>Privacy Infrastructure</GradientText>
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Everything you need to issue, verify, and manage privacy-preserving credentials at scale.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={Lock}
              title="Zero-Knowledge Proofs"
              description="Verify attributes without revealing data. Users prove claims cryptographically without exposing underlying information."
              gradient="bg-gradient-to-br from-primary-500 to-primary-600"
            />
            <FeatureCard
              icon={Database}
              title="Blockchain Anchored"
              description="Credentials are cryptographically committed to Base L2. Tamper-proof, auditable, and decentralized with 99% lower costs than Ethereum L1."
              gradient="bg-gradient-to-br from-purple-500 to-purple-600"
            />
            <FeatureCard
              icon={Eye}
              title="Privacy by Design"
              description="Personal data never leaves the user's device. Only cryptographic commitments are stored on-chain."
              gradient="bg-gradient-to-br from-success-500 to-success-600"
            />
            <FeatureCard
              icon={Server}
              title="Multi-Tenant Platform"
              description="Isolated environments for each organization with granular API key permissions and usage tracking."
              gradient="bg-gradient-to-br from-amber-500 to-amber-600"
            />
            <FeatureCard
              icon={Zap}
              title="Sub-Second Proofs"
              description="Generate ZK proofs in under 50ms using WebAssembly. Verify on-chain with full Groth16 security."
              gradient="bg-gradient-to-br from-rose-500 to-rose-600"
            />
            <FeatureCard
              icon={Code2}
              title="Developer First"
              description="TypeScript SDK, REST APIs, and comprehensive docs. Go from zero to production in hours."
              gradient="bg-gradient-to-br from-indigo-500 to-indigo-600"
            />
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* CREDENTIAL TYPES SECTION */}
      {/* ================================================================== */}
      <section id="credentials" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Extensible Credential Framework, <GradientText>Endless Possibilities</GradientText>
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              ZKCN supports any verifiable credential type. Six types are live today, each powered by its own optimized zero-knowledge circuit.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <CredentialTypeCard
              type="AGE_VERIFICATION"
              circuit="ARP Circuit"
              title="Age Range Proof"
              description="Prove you're within an age range (e.g., over 18, 21-65) without revealing your actual birthdate."
              useCases={[
                "Adult content platforms",
                "Online gambling & casinos",
                "Alcohol & tobacco e-commerce",
                "Age-restricted gaming",
                "Financial services onboarding",
              ]}
              gradient="bg-gradient-to-br from-primary-500 to-primary-600"
              icon={Users}
            />
            <CredentialTypeCard
              type="PERMISSIONS"
              circuit="PMP Circuit"
              title="Permissions Proof"
              description="Prove you hold specific permissions without exposing your full permission set or access details."
              useCases={[
                "Data access verification",
                "API tier authentication",
                "Resource sharing controls",
                "Multi-tenant permission checks",
                "Role-based access proofs",
              ]}
              gradient="bg-gradient-to-br from-success-500 to-success-600"
              icon={FileCheck}
            />
            <CredentialTypeCard
              type="IDENTITY"
              circuit="IDP Circuit"
              title="Identity Proof"
              description="Prove identity attributes like nationality or residency without revealing personal documents."
              useCases={[
                "Residency verification",
                "Nationality proofs",
                "Membership checks",
                "Travel document verification",
                "Club memberships",
              ]}
              gradient="bg-gradient-to-br from-purple-500 to-purple-600"
              icon={Shield}
            />
            <CredentialTypeCard
              type="EDUCATION"
              circuit="EDP Circuit"
              title="Education Degree Proof"
              description="Prove academic credentials like degree level and field of study without revealing transcripts or GPA."
              useCases={[
                "Employer degree verification",
                "Professional certification proof",
                "Graduate program admissions",
                "Skills & qualification attestation",
                "Anti-diploma-fraud protection",
              ]}
              gradient="bg-gradient-to-br from-amber-500 to-orange-500"
              icon={Award}
            />
            <CredentialTypeCard
              type="KYC_VERIFICATION"
              circuit="KVP Circuit"
              title="KYC Verification Proof"
              description="Prove you've passed KYC with a trusted provider without re-sharing identity documents every time."
              useCases={[
                "Fintech & neobank onboarding",
                "DeFi protocol compliance",
                "Crypto exchange access",
                "Cross-border remittance",
                "Reusable identity verification",
              ]}
              gradient="bg-gradient-to-br from-teal-500 to-cyan-500"
              icon={Fingerprint}
            />
            <CredentialTypeCard
              type="PROFESSIONAL_LICENSE"
              circuit="PLP Circuit"
              title="Professional License Proof"
              description="Prove active professional licenses and certifications without exposing license numbers or personal details."
              useCases={[
                "Medical license verification",
                "Legal bar admissions",
                "Engineering certifications",
                "Financial advisor licenses",
                "Real estate agent verification",
              ]}
              gradient="bg-gradient-to-br from-teal-500 to-cyan-500"
              icon={BadgeCheck}
            />
          </div>

          {/* Custom credential CTA */}
          <div className="mt-16 max-w-3xl mx-auto">
            <div className="group bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-sm border-2 border-dashed border-slate-200 hover:border-primary-300 transition-all duration-300 overflow-hidden flex flex-col items-center justify-center p-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <Sparkles className="w-7 h-7 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Custom Credential Types</h3>
              <p className="text-sm text-slate-500 max-w-lg">Need a specific credential type for your industry? We build custom ZK circuits for enterprise partners.</p>
              <Link to="/contact" className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1">
                Contact Us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* USER JOURNEY SECTION */}
      {/* ================================================================== */}
      <section id="journey" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Your Journey with <GradientText>ZKCN</GradientText>
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Whether you're issuing credentials, verifying proofs, or managing your own data—we've got you covered.
            </p>
          </div>

          {/* Stakeholder selector */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {[
              { id: "issuer", label: "I'm an Issuer", icon: Building2, description: "Issue credentials to users" },
              { id: "verifier", label: "I'm a Verifier", icon: ShieldCheck, description: "Verify proofs from users" },
              { id: "subject", label: "I'm a User", icon: Users, description: "Manage my credentials" },
            ].map((role) => (
              <button
                key={role.id}
                onClick={() => setSelectedStakeholder(role.id as Stakeholder)}
                className={`flex items-center gap-4 px-6 py-4 rounded-xl border-2 transition-all duration-200 ${
                  selectedStakeholder === role.id
                    ? "border-primary-500 bg-primary-50 shadow-lg shadow-primary-500/10"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md"
                }`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                  selectedStakeholder === role.id 
                    ? "bg-primary-500 text-white" 
                    : "bg-slate-100 text-slate-500"
                }`}>
                  <role.icon className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <div className={`font-semibold ${selectedStakeholder === role.id ? "text-primary-700" : "text-slate-700"}`}>
                    {role.label}
                  </div>
                  <div className="text-sm text-slate-500">{role.description}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Journey steps */}
          {selectedStakeholder && (
            <div className="max-w-2xl mx-auto bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-200 shadow-lg animate-fade-in">
              <h3 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center">
                  {selectedStakeholder === "issuer" && <Building2 className="w-5 h-5 text-white" />}
                  {selectedStakeholder === "verifier" && <ShieldCheck className="w-5 h-5 text-white" />}
                  {selectedStakeholder === "subject" && <Users className="w-5 h-5 text-white" />}
                </div>
                {selectedStakeholder === "issuer" && "Issuer Journey"}
                {selectedStakeholder === "verifier" && "Verifier Journey"}
                {selectedStakeholder === "subject" && "User Journey"}
              </h3>
              <div className="space-y-0">
                {journeys[selectedStakeholder].map((step, index) => (
                  <JourneyStep
                    key={index}
                    step={index + 1}
                    title={step.title}
                    description={step.description}
                    isLast={index === journeys[selectedStakeholder].length - 1}
                  />
                ))}
              </div>
              <Link
                to="/signin"
                className="mt-8 w-full py-4 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary-500/25"
              >
                Get Started as {selectedStakeholder === "issuer" ? "Issuer" : selectedStakeholder === "verifier" ? "Verifier" : "User"}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          )}

          {!selectedStakeholder && (
            <div className="text-center py-16 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Select a role above to see your personalized journey</p>
            </div>
          )}
        </div>
      </section>

      {/* ================================================================== */}
      {/* STATS BANNER */}
      {/* ================================================================== */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <StatCard value="6" label="Credential Types" icon={FileCheck} />
            <StatCard value="7" label="ZK Circuits" icon={Lock} />
            <StatCard value="99.9%" label="Uptime SLA" icon={TrendingUp} />
            <StatCard value="<50ms" label="Proof Generation" icon={Clock} />
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* ENTERPRISE SECTION */}
      {/* ================================================================== */}
      <section id="enterprise" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full text-amber-700 text-sm font-medium mb-6">
                <Star className="w-4 h-4" />
                Enterprise Ready
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                Built for <GradientText>Enterprise Scale</GradientText>
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                From startups to Fortune 500 companies, ZKCN scales with your needs.
                Get dedicated support, custom integrations, and enterprise-grade SLAs.
              </p>

              <div className="mt-8 space-y-4">
                {[
                  "Dedicated customer success manager",
                  "Custom contract and SLA terms",
                  "On-premise deployment options",
                  "Priority support with 1-hour response",
                  "SOC 2 Type II certification",
                  "HIPAA BAA available",
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-success-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-success-600" />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-xl transition-colors shadow-lg"
                >
                  Contact Sales
                </Link>
                <Link
                  to="/pricing"
                  className="px-8 py-4 border border-slate-300 hover:border-slate-400 text-slate-700 font-semibold rounded-xl transition-colors text-center"
                >
                  View Pricing
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-success-500/10 rounded-3xl blur-2xl" />
              <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 border border-slate-700 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <Star className="w-6 h-6 text-amber-400" />
                  <span className="text-white font-semibold text-lg">Enterprise Plan</span>
                </div>
                <div className="space-y-4">
                  {[
                    { label: "Monthly Verifications", value: "Unlimited" },
                    { label: "API Rate Limit", value: "100K req/min" },
                    { label: "Team Members", value: "Unlimited" },
                    { label: "Data Retention", value: "7 years" },
                    { label: "Support Response", value: "1 hour" },
                    { label: "Custom Integrations", value: "Included" },
                  ].map((item, index) => (
                    <div key={index} className="flex justify-between py-3 border-b border-slate-700 last:border-0">
                      <span className="text-slate-400">{item.label}</span>
                      <span className="text-white font-semibold">{item.value}</span>
                    </div>
                  ))}
                </div>

                {/* Tech stack */}
                <div className="mt-8 pt-6 border-t border-slate-700">
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-4">Built With</div>
                  <div className="flex flex-wrap gap-2">
                    <TechItem icon={Database} name="Base L2" />
                    <TechItem icon={Lock} name="Groth16" />
                    <TechItem icon={Code2} name="TypeScript" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* FINAL CTA SECTION */}
      {/* ================================================================== */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-primary-50/30 to-success-50/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">
            Ready to Build with <GradientText>Privacy</GradientText>?
          </h2>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            Join hundreds of enterprises using ZKCN for privacy-preserving verification.
            Start for free, scale when you're ready.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signin"
              className="group w-full sm:w-auto px-10 py-5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white text-lg font-semibold rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              Start Building Free
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <a
              href="https://github.com/mohamadsolouki/ZKPCMS"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-10 py-5 bg-white hover:bg-slate-50 text-slate-700 text-lg font-semibold rounded-xl border border-slate-200 shadow-sm hover:shadow-md flex items-center justify-center gap-2 transition-all"
            >
              View on GitHub
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
